<template>
  <div class="rag-panel">
    <div v-if="!currentKnowledgeBase" class="knowledge-shell">
      <div class="panel-toolbar">
        <div>
          <div class="panel-title">知识库</div>
        </div>
        <div class="toolbar-actions">
	          <el-button plain size="small" :disabled="uploading" data-track-text="刷新知识库列表" aria-label="刷新知识库列表" @click="$emit('refresh')">刷新</el-button>
	          <el-button type="primary" size="small" data-track-text="新建知识库" aria-label="新建知识库" @click="openCreateDialog">新建知识库</el-button>
        </div>
      </div>
      <div class="quota-notice">
        <i class="el-icon-info"></i>
        <span>每位用户可享 10GB 知识库存储配额，建议优先上传高价值资料，并定期清理不再使用的文件。</span>
      </div>

      <div v-loading="loading" class="table-wrap">
        <div v-if="!loading && !knowledgeBases.length" class="empty-wrap">
          <el-empty description="当前还没有知识库" />
	          <el-button type="primary" size="small" data-track-text="新建第一个知识库" aria-label="新建第一个知识库" @click="openCreateDialog">新建第一个知识库</el-button>
        </div>
        <el-table v-else :data="knowledgeBases" class="rag-table" :header-cell-style="headerCellStyle">
          <el-table-column prop="name" label="名称" min-width="200" show-overflow-tooltip />
          <el-table-column label="类型" width="120">
            <template slot-scope="scope">
              <span>{{ formatKnowledgeBaseType(scope.row.type) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="文档数" width="110">
            <template slot-scope="scope">
              <span>{{ scope.row.documentCount || scope.row.knowledgeCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column label="处理中" width="110">
            <template slot-scope="scope">
              <span>{{ scope.row.processingCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column label="更新时间" min-width="180">
            <template slot-scope="scope">
              <span>{{ formatDisplayDate(scope.row.updateTime || scope.row.createTime) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="描述" min-width="240" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.description || '文档型知识库' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="320" fixed="right">
            <template slot-scope="scope">
              <div class="row-actions">
	                <el-button type="text" data-track-text="进入知识库" aria-label="进入知识库" @click="$emit('open-knowledge-base', scope.row)">进入</el-button>
	                <el-button type="text" :disabled="uploading" data-track-text="知识库上传文件" aria-label="知识库上传文件" @click="openFileUpload(scope.row)">上传文件</el-button>
	                <el-button type="text" :disabled="uploading" data-track-text="知识库上传目录" aria-label="知识库上传目录" @click="openFolderUpload(scope.row)">上传目录</el-button>
	                <el-button type="text" class="danger-text" data-track-text="删除知识库" aria-label="删除知识库" @click="$emit('delete-knowledge-base', scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <div v-else class="knowledge-shell knowledge-detail">
      <div class="panel-toolbar detail-toolbar">
        <div class="detail-title-wrap">
          <div class="detail-back" data-track-text="返回知识库列表" aria-label="返回知识库列表" @click="$emit('back-to-list')">
            <i class="el-icon-arrow-left"></i>
            <span>返回知识库列表</span>
          </div>
          <div class="panel-title">{{ currentKnowledgeBase.name }}</div>
          <div class="panel-subtitle">
            {{ currentKnowledgeBase.description || '文档型知识库，上传文档后可在对话中检索使用。' }}
          </div>
        </div>
        <div class="toolbar-actions">
	          <el-button plain size="small" :disabled="uploading" data-track-text="刷新知识库详情" aria-label="刷新知识库详情" @click="$emit('refresh-detail')">刷新</el-button>
	          <el-button plain size="small" :disabled="uploading" data-track-text="编辑知识库配置" aria-label="编辑知识库配置" @click="openEditDialog">编辑配置</el-button>
	          <el-button type="primary" size="small" :loading="uploading" data-track-text="详情上传知识库文件" aria-label="详情上传知识库文件" @click="openFileUpload(currentKnowledgeBase)">上传文件</el-button>
	          <el-button plain size="small" :disabled="uploading" data-track-text="详情上传知识库目录" aria-label="详情上传知识库目录" @click="openFolderUpload(currentKnowledgeBase)">上传目录</el-button>
	          <el-button type="danger" plain size="small" :disabled="uploading" data-track-text="详情删除知识库" aria-label="详情删除知识库" @click="$emit('delete-knowledge-base', currentKnowledgeBase)">删除知识库</el-button>
        </div>
      </div>
      <div class="quota-notice">
        <i class="el-icon-info"></i>
        <span>当前账号可用知识库存储配额为 10GB，上传前建议确认文件有效性，避免占用不必要空间。</span>
      </div>

      <div v-loading="detailLoading" class="detail-summary">
        <div class="summary-card">
          <div class="summary-label">知识库类型</div>
          <div class="summary-value">{{ formatKnowledgeBaseType(currentKnowledgeBase.type) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">文档数量</div>
          <div class="summary-value">{{ currentKnowledgeBase.documentCount || currentKnowledgeBase.knowledgeCount || 0 }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">处理中</div>
          <div class="summary-value">{{ currentKnowledgeBase.processingCount || 0 }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">更新时间</div>
          <div class="summary-value">{{ formatDisplayDate(currentKnowledgeBase.updateTime || currentKnowledgeBase.createTime) }}</div>
        </div>
      </div>

      <div class="config-card">
        <div class="section-title">基础配置</div>
        <div class="config-grid">
          <div class="config-item">
            <div class="config-label">解析策略</div>
            <div class="config-value">{{ formatParserStrategy(currentKnowledgeBase.parserStrategy || 'auto') }}</div>
          </div>
          <div class="config-item">
            <div class="config-label">切块策略</div>
            <div class="config-value">{{ formatChunkStrategy(currentKnowledgeBase.chunkStrategy || 'general') }}</div>
          </div>
          <div class="config-item config-item-wide">
            <div class="config-label">描述</div>
            <div class="config-value">{{ currentKnowledgeBase.description || '未设置描述' }}</div>
          </div>
        </div>
      </div>

      <div class="document-card">
        <div class="document-header">
          <div class="section-title">文档列表</div>
	          <el-button plain size="small" data-track-text="刷新知识库文档状态" aria-label="刷新知识库文档状态" @click="$emit('refresh-documents')">刷新状态</el-button>
        </div>
        <div v-loading="documentLoading" class="document-table-wrap">
          <div v-if="!documentLoading && !documents.length" class="empty-wrap document-empty">
            <el-empty description="当前还没有上传文档" />
          </div>
          <el-table v-else :data="documents" class="rag-table" :header-cell-style="headerCellStyle">
            <el-table-column prop="name" label="文件名" min-width="240" show-overflow-tooltip />
            <el-table-column label="来源类型" width="120">
              <template slot-scope="scope">
                <span>{{ formatDocumentSourceType(scope.row.sourceType) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="解析状态" width="140">
              <template slot-scope="scope">
                <span :class="['status-tag', normalizeStatusClass(scope.row.status)]">
                  {{ formatDocumentStatus(scope.row.status) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="文件大小" width="120">
              <template slot-scope="scope">
                <span>{{ formatFileSize(scope.row.fileSize) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="更新时间" min-width="180">
              <template slot-scope="scope">
                <span>{{ formatDisplayDate(scope.row.updateTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="错误信息" min-width="220" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ scope.row.errorMessage || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template slot-scope="scope">
	                <el-button type="text" class="danger-text" data-track-text="删除知识库文档" aria-label="删除知识库文档" @click="$emit('delete-document', scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <el-dialog class="rag-dialog" title="新建知识库" :visible.sync="createDialogVisible" width="560px">
      <el-form :model="createForm" label-width="120px">
        <el-form-item label="知识库类型">
          <el-input value="文档知识库" disabled />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="createForm.name" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="createForm.description" type="textarea" :rows="4" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="解析策略">
          <el-select v-model="createForm.parserStrategy" placeholder="选择解析策略">
            <el-option v-for="item in parserStrategyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="切块策略">
          <el-select v-model="createForm.chunkStrategy" placeholder="选择切块策略">
            <el-option v-for="item in chunkStrategyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
	        <el-button data-track-text="取消新建知识库" aria-label="取消新建知识库" @click="createDialogVisible = false">取消</el-button>
	        <el-button type="primary" data-track-text="提交新建知识库" aria-label="提交新建知识库" @click="submitCreate">创建</el-button>
      </div>
    </el-dialog>

    <el-dialog class="rag-dialog" title="编辑知识库配置" :visible.sync="editDialogVisible" width="560px">
      <el-form :model="editForm" label-width="120px">
        <el-form-item label="知识库类型">
          <el-input value="文档知识库" disabled />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="editForm.name" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="editForm.description" type="textarea" :rows="4" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="解析策略">
          <el-select v-model="editForm.parserStrategy" placeholder="选择解析策略">
            <el-option v-for="item in parserStrategyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="切块策略">
          <el-select v-model="editForm.chunkStrategy" placeholder="选择切块策略">
            <el-option v-for="item in chunkStrategyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
	        <el-button data-track-text="取消编辑知识库" aria-label="取消编辑知识库" @click="editDialogVisible = false">取消</el-button>
	        <el-button type="primary" data-track-text="保存知识库配置" aria-label="保存知识库配置" @click="submitEdit">保存</el-button>
      </div>
    </el-dialog>

    <input
      ref="fileUploadInput"
      class="native-upload-input"
      type="file"
      :accept="fileUploadAccept"
      multiple
      @change="handleFileSelection"
    >
    <input
      ref="folderUploadInput"
      class="native-upload-input"
      type="file"
      :accept="fileUploadAccept"
      webkitdirectory
      @change="handleFolderSelection"
    >
  </div>
</template>

<script>
export default {
  name: 'RagKnowledgeBasePanel',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    knowledgeBases: {
      type: Array,
      default: () => []
    },
    currentKnowledgeBase: {
      type: Object,
      default: null
    },
    fileUploadAccept: {
      type: String,
      default: ''
    },
    uploading: {
      type: Boolean,
      default: false
    },
    detailLoading: {
      type: Boolean,
      default: false
    },
    documentLoading: {
      type: Boolean,
      default: false
    },
    documents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      createDialogVisible: false,
      editDialogVisible: false,
      pendingUploadTarget: null,
      createForm: this.buildDefaultForm(),
      editForm: this.buildDefaultForm(),
      parserStrategyOptions: [
        { label: '自动解析', value: 'auto' },
        { label: '深度文档解析', value: 'deepdoc' },
        { label: 'OCR 识别', value: 'ocr' }
      ],
      chunkStrategyOptions: [
        { label: '通用切块', value: 'general' },
        { label: '按句切块', value: 'sentence' },
        { label: '语义切块', value: 'semantic' }
      ]
    }
  },
  watch: {
    currentKnowledgeBase: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.editForm = this.buildDefaultForm()
          return
        }
        this.editForm = this.buildFormFromKnowledgeBase(value)
      }
    }
  },
  methods: {
    buildDefaultForm() {
      return {
        name: '',
        description: '',
        type: 'DOCUMENT',
        parserStrategy: 'auto',
        chunkStrategy: 'general'
      }
    },
    buildFormFromKnowledgeBase(knowledgeBase) {
      return {
        name: knowledgeBase.name || '',
        description: knowledgeBase.description || '',
        type: knowledgeBase.type || 'DOCUMENT',
        parserStrategy: knowledgeBase.parserStrategy || 'auto',
        chunkStrategy: knowledgeBase.chunkStrategy || 'general'
      }
    },
    openCreateDialog() {
      this.createForm = this.buildDefaultForm()
      this.createDialogVisible = true
    },
    openEditDialog() {
      this.editForm = this.buildFormFromKnowledgeBase(this.currentKnowledgeBase || {})
      this.editDialogVisible = true
    },
    openFileUpload(row) {
      this.openNativeUpload(row, 'file')
    },
    openFolderUpload(row) {
      this.openNativeUpload(row, 'folder')
    },
    openNativeUpload(row, mode) {
      if (!row || !row.id) {
        this.$message.error('未找到知识库')
        return
      }
      this.pendingUploadTarget = {
        row,
        knowledgeBaseId: row.id,
        mode
      }
      const refName = mode === 'folder' ? 'folderUploadInput' : 'fileUploadInput'
      const input = this.$refs[refName]
      if (input) {
        input.value = ''
        input.click()
      }
    },
    handleFileSelection(event) {
      this.handleNativeSelection(event, false)
    },
    handleFolderSelection(event) {
      this.handleNativeSelection(event, true)
    },
    handleNativeSelection(event, isFolder) {
      const files = Array.from((event.target && event.target.files) || [])
      if (!files.length) {
        this.resetNativeInput(event.target)
        return
      }
      if (!this.pendingUploadTarget || !this.pendingUploadTarget.knowledgeBaseId) {
        this.$message.error('未找到知识库')
        this.resetNativeInput(event.target)
        return
      }
      this.$emit('upload', {
        row: this.pendingUploadTarget.row,
        knowledgeBaseId: this.pendingUploadTarget.knowledgeBaseId,
        files,
        isFolder
      })
      this.pendingUploadTarget = null
      this.resetNativeInput(event.target)
    },
    resetNativeInput(input) {
      if (input) {
        input.value = ''
      }
    },
    submitCreate() {
      const payload = this.buildPayload(this.createForm)
      if (!payload) {
        return
      }
      this.$emit('create-knowledge-base', payload)
      this.createDialogVisible = false
    },
    submitEdit() {
      const payload = this.buildPayload(this.editForm)
      if (!payload || !this.currentKnowledgeBase) {
        return
      }
      this.$emit('update-knowledge-base', {
        knowledgeBaseId: this.currentKnowledgeBase.id,
        ...payload
      })
      this.editDialogVisible = false
    },
    buildPayload(form) {
      const name = (form.name || '').trim()
      if (!name) {
        this.$message.error('请输入知识库名称')
        return null
      }
      return {
        name,
        description: (form.description || '').trim(),
        type: 'DOCUMENT',
        parserStrategy: form.parserStrategy || 'auto',
        chunkStrategy: form.chunkStrategy || 'general'
      }
    },
    normalizeStatusClass(status) {
      const value = String(status || '').toLowerCase()
      if (value.indexOf('success') > -1 || value.indexOf('done') > -1 || value.indexOf('completed') > -1) {
        return 'success'
      }
      if (value.indexOf('fail') > -1 || value.indexOf('error') > -1) {
        return 'danger'
      }
      return 'processing'
    },
    formatDocumentSourceType(sourceType) {
      const rawValue = String(sourceType || '').trim()
      const normalizedValue = rawValue.toLowerCase()
      if (!rawValue || normalizedValue === 'unknown') {
        return '未知'
      }
      if (['file', 'upload'].includes(normalizedValue)) {
        return '文件上传'
      }
      if (['url', 'web', 'link', 'website'].includes(normalizedValue)) {
        return '网页导入'
      }
      if (normalizedValue === 'text') {
        return '文本录入'
      }
      if (normalizedValue === 'faq') {
        return 'FAQ'
      }
      if (normalizedValue === 'api') {
        return '接口导入'
      }
      return rawValue
    },
    formatDocumentStatus(status) {
      const rawValue = String(status || '').trim()
      const normalizedValue = rawValue.toLowerCase()
      if (!rawValue || normalizedValue === 'unknown') {
        return '未知'
      }
      if (['success', 'done', 'completed', 'succeeded'].includes(normalizedValue)) {
        return '解析完成'
      }
      if (['processing', 'parsing', 'running', 'indexing', 'in_progress'].includes(normalizedValue)) {
        return '解析中'
      }
      if (['pending', 'queued', 'waiting'].includes(normalizedValue)) {
        return '排队中'
      }
      if (['fail', 'failed', 'error'].includes(normalizedValue)) {
        return '解析失败'
      }
      if (['cancelled', 'canceled'].includes(normalizedValue)) {
        return '已取消'
      }
      return rawValue
    },
    formatFileSize(size) {
      const value = Number(size || 0)
      if (!Number.isFinite(value) || value <= 0) {
        return '-'
      }
      if (value < 1024) {
        return `${Math.round(value)} B`
      }
      if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`
      }
      if (value < 1024 * 1024 * 1024) {
        return `${(value / (1024 * 1024)).toFixed(1)} MB`
      }
      return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`
    },
    formatKnowledgeBaseType(type) {
      if (String(type || '').toUpperCase() === 'FAQ') {
        return 'FAQ'
      }
      return '文档知识库'
    },
    formatParserStrategy(strategy) {
      const option = this.parserStrategyOptions.find(item => item.value === strategy)
      return option ? option.label : '自动解析'
    },
    formatChunkStrategy(strategy) {
      const option = this.chunkStrategyOptions.find(item => item.value === strategy)
      return option ? option.label : '通用切块'
    },
    formatDisplayDate(value) {
      if (!value) {
        return '-'
      }
      const text = String(value).trim()
      if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
        return text.slice(0, 10)
      }
      const normalized = text.replace(' ', 'T')
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) {
        return text
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    headerCellStyle() {
      return {
        background: '#f8f9fb',
        color: 'var(--foreground-secondary)',
        fontWeight: 'bold'
      }
    }
  }
}
</script>

<style lang="less" scoped>
.rag-panel {
  min-height: 620px;
}

.knowledge-shell {
  min-height: 620px;
}

.panel-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.panel-title {
  color: var(--foreground-secondary);
  font-size: 24px;
  font-weight: bold;
  line-height: 32px;
}

.panel-subtitle {
  margin-top: 8px;
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 22px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.native-upload-input {
  display: none;
}

.quota-notice {
  margin-top: 18px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  background: #f5fbff;
  color: #4f6472;
  font-size: 13px;
  line-height: 22px;
}

.quota-notice i {
  margin-top: 3px;
  color: var(--primary);
  font-size: 14px;
}

.table-wrap,
.config-card,
.document-card {
  margin-top: 24px;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
}

.table-wrap,
.document-table-wrap {
  min-height: 470px;
}

.empty-wrap {
  min-height: 470px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.document-empty {
  min-height: 360px;
}

.detail-toolbar {
  align-items: center;
}

.detail-title-wrap {
  min-width: 0;
}

.detail-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-dark);
  font-size: 14px;
  cursor: pointer;
}

.detail-summary {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.summary-card {
  padding: 18px 20px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
}

.summary-label,
.config-label {
  color: var(--disabled-text);
  font-size: 13px;
  line-height: 20px;
}

.summary-value,
.config-value {
  margin-top: 8px;
  color: var(--foreground-secondary);
  font-size: 16px;
  line-height: 24px;
  word-break: break-word;
}

.section-title {
  color: var(--foreground-secondary);
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;
}

.config-card,
.document-card {
  padding: 20px;
}

.config-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 24px;
}

.config-item-wide {
  grid-column: span 2;
}

.document-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.document-table-wrap {
  margin-top: 18px;
}

.rag-table {
  width: 100%;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  background: var(--primary-alpha-08);
  color: var(--primary-dark);
}

.status-tag.success {
  background: #edf7ef;
  color: #2f8a57;
}

.status-tag.danger {
  background: #fbeeee;
  color: #c45656;
}

.danger-text {
  color: #c45656;
}

::v-deep .el-table td,
::v-deep .el-table th {
  padding: 12px 0;
}

::v-deep .el-table::before {
  height: 0;
}

::v-deep .el-button--text {
  color: var(--primary-dark);
  padding: 0;
}

::v-deep .el-button--text:hover,
::v-deep .el-button--text:focus {
  color: var(--primary-dark);
}

::v-deep .el-select {
  width: 100%;
}

@media (max-width: 1480px) {
  .detail-summary,
  .config-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .rag-panel,
  .knowledge-shell {
    width: 100%;
    min-width: 0;
    min-height: 0;
  }

  .panel-toolbar,
  .detail-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .panel-title {
    font-size: 20px;
    line-height: 28px;
    word-break: break-word;
  }

  .panel-subtitle {
    margin-top: 5px;
    font-size: 13px;
    line-height: 20px;
  }

  .toolbar-actions {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .toolbar-actions ::v-deep .el-button {
    width: 100%;
    min-width: 0;
    margin-left: 0;
  }

  .quota-notice {
    margin-top: 12px;
    padding: 10px 12px;
  }

  .table-wrap,
  .config-card,
  .document-card {
    margin-top: 16px;
  }

  .table-wrap,
  .document-table-wrap,
  .empty-wrap {
    min-height: 320px;
  }

  .document-empty {
    min-height: 260px;
  }

  .table-wrap,
  .document-table-wrap {
    max-width: 100%;
    overflow: hidden;
  }

  ::v-deep .el-table__body-wrapper {
    overflow-x: auto !important;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }

  ::v-deep .el-table__fixed-right,
  ::v-deep .el-table__fixed-right-patch {
    display: none;
  }

  .detail-summary {
    margin-top: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-card,
  .config-card,
  .document-card {
    padding: 14px;
  }

  .config-grid {
    margin-top: 14px;
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
  }

  .config-item-wide {
    grid-column: auto;
  }

  .document-header {
    align-items: flex-start;
  }

  ::v-deep .rag-dialog .el-form-item__label {
    width: auto !important;
    float: none;
    padding: 0 0 6px;
    line-height: 20px;
    text-align: left;
  }

  ::v-deep .rag-dialog .el-form-item__content {
    margin-left: 0 !important;
  }
}

@media (max-width: 480px) {
  .toolbar-actions,
  .detail-summary {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
